import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Shuffle, Trash2, Lock, X, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  getJournalEntries,
  addJournalEntry,
  deleteJournalEntry,
  saveReflectionToEntry,
  getProtectItems,
} from "@/lib/storage";
import { randomPrompt } from "@/lib/journalPrompts";
import { askReflection, reflectionCompanionAvailable } from "@/lib/aiReflect";
import { useAutoSavedDraft } from "@/lib/useAutoSavedDraft";
import AutoSaveIndicator from "@/components/AutoSaveIndicator";
import { JOURNAL, JOURNAL_REFLECT } from "@/constants/testIds";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [composing, setComposing] = useState(false);
  const {
    value: draft,
    setValue: setDraft,
    clear: clearDraft,
  } = useAutoSavedDraft("journal", { text: "", usePrompt: false, prompt: randomPrompt() });
  const text = draft.text;
  const usePrompt = draft.usePrompt;
  const prompt = draft.prompt;
  const setText = (next) => setDraft((d) => ({ ...d, text: typeof next === "function" ? next(d.text) : next }));
  const setUsePrompt = (next) => setDraft((d) => ({ ...d, usePrompt: next }));
  const setPrompt = (next) => setDraft((d) => ({ ...d, prompt: typeof next === "function" ? next(d.prompt) : next }));
  const [loadingReflectId, setLoadingReflectId] = useState(null);

  useEffect(() => {
    setEntries(getJournalEntries());
  }, []);

  // Reopen composer if a restored draft has content.
  useEffect(() => {
    if (draft.text && draft.text.trim().length > 0) setComposing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (!text.trim()) return;
    const entry = addJournalEntry({
      text,
      prompt: usePrompt ? prompt : null,
    });
    setEntries((es) => [entry, ...es]);
    clearDraft();
    setComposing(false);
    toast.success("Entry saved to your device.", {
      description: "Saved locally. Nothing is sent unless you choose an external reflection feature.",
    });
  };

  const handleDelete = (id) => {
    deleteJournalEntry(id);
    setEntries((es) => es.filter((e) => e.id !== id));
    toast("Entry deleted.");
  };

  const handleReflect = async (entry) => {
    if (loadingReflectId) return;
    setLoadingReflectId(entry.id);
    try {
      const protectList = getProtectItems().map((p) => p.label);
      const { response } = await askReflection({
        text: entry.text,
        mode: "journal",
        protectList,
      });
      saveReflectionToEntry(entry.id, response);
      setEntries((es) =>
        es.map((e) => (e.id === entry.id ? { ...e, reflection: response } : e))
      );
    } catch (err) {
      const msg =
        err?.userMessage ||
        err?.response?.data?.detail ||
        "The companion couldn't reach you just now.";
      toast(msg, { description: "Try again in a moment." });
    } finally {
      setLoadingReflectId(null);
    }
  };

  return (
    <PageContainer testid={JOURNAL.root}>
      <PageHeader
        title="Reflection Journal"
        subtitle="A private space for whatever's on your mind — without editing, without an audience."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={JOURNAL.exit}
      />

      {/* Privacy badge */}
      <div
        className="rounded-2xl p-3 mb-5 flex items-start gap-2.5"
        style={{ background: "rgba(74,32,64,0.05)" }}
      >
        <Lock size={14} className="mt-0.5 shrink-0" style={{ color: "var(--un-plum)" }} />
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--un-text-2)" }}
        >
          Entries are saved to <strong>this device only</strong>. No account or sync is required.
          AI reflection appears only when an independent processing connection is enabled. If you choose to use it, only
          that entry and your Protect List labels are sent for processing to generate a response. A random app-install
          identifier is sent to the UnAnonymous backend for temporary abuse limits — see <a href="/privacy" className="underline underline-offset-2" style={{ color: "var(--un-plum)" }}>Privacy</a>.
        </p>
      </div>

      {/* New entry CTA */}
      {!composing && (
        <SoftButton
          data-testid={JOURNAL.newEntryBtn}
          variant="primary"
          size="lg"
          iconLeft={<Plus size={18} />}
          onClick={() => {
            setComposing(true);
            setPrompt(randomPrompt());
          }}
          className="w-full mb-6"
        >
          New entry
        </SoftButton>
      )}

      {/* Composer */}
      <AnimatePresence>
        {composing && (
          <motion.div
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: 8, height: 0 }}
            transition={{ duration: 0.3 }}
            className="un-card p-5 mb-6 overflow-hidden"
          >
            {/* Prompt toggle */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <button
                data-testid={JOURNAL.promptToggle}
                onClick={() => setUsePrompt((v) => !v)}
                className="flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full un-press"
                style={{
                  background: usePrompt
                    ? "rgba(74,32,64,0.10)"
                    : "rgba(74,32,64,0.04)",
                  color: usePrompt
                    ? "var(--un-plum)"
                    : "var(--un-text-2)",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {usePrompt ? <Check size={12} /> : <Plus size={12} />}
                {usePrompt ? "Using prompt" : "Use a prompt"}
              </button>
              {usePrompt && (
                <button
                  onClick={() => setPrompt(randomPrompt())}
                  aria-label="New prompt"
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full un-press"
                  style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
                >
                  <Shuffle size={12} />
                  Shuffle
                </button>
              )}
            </div>

            {usePrompt && (
              <p
                className="italic text-[17px] mb-3 leading-snug"
                style={{
                  color: "var(--un-burgundy)",
                  fontFamily: "Cormorant Garamond, serif",
                }}
              >
                {prompt}
              </p>
            )}

            <textarea
              data-testid={JOURNAL.textarea}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write whatever feels true. No editing required."
              rows={7}
              className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] leading-relaxed"
              style={{
                background: "var(--un-bg-2)",
                color: "var(--un-text-1)",
                fontFamily: "DM Sans, sans-serif",
              }}
              autoFocus
            />

            <div className="flex items-center justify-between gap-2 mt-3">
              <AutoSaveIndicator watch={text} />
              <div className="flex items-center gap-2">
              <SoftButton
                data-testid={JOURNAL.cancelBtn}
                variant="ghost"
                size="sm"
                onClick={() => {
                  setComposing(false);
                  // Buffer is preserved — returning restores the draft.
                }}
                iconLeft={<X size={14} />}
              >
                Cancel
              </SoftButton>
              <SoftButton
                data-testid={JOURNAL.saveBtn}
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={!text.trim()}
                iconLeft={<Check size={14} />}
              >
                Save entry
              </SoftButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entries list */}
      {entries.length === 0 ? (
        <div
          data-testid={JOURNAL.emptyState}
          className="text-center py-12 px-4"
        >
          <div
            className="mx-auto w-16 h-16 rounded-full mb-4 flex items-center justify-center"
            style={{ background: "rgba(74,32,64,0.06)" }}
          >
            <Lock size={22} style={{ color: "var(--un-plum)" }} />
          </div>
          <p
            className="text-[17px] font-medium mb-1.5"
            style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
          >
            Nothing here yet
          </p>
          <p
            className="italic text-[16px] leading-relaxed"
            style={{
              color: "var(--un-text-3)",
              fontFamily: "Cormorant Garamond, serif",
            }}
          >
            One honest sentence is a beginning.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 mb-10">
          {entries.map((e, idx) => (
            <motion.div
              key={e.id}
              data-testid={`${JOURNAL.entryPrefix}${e.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.03 }}
              className="un-card p-5"
            >
              <div className="flex items-center justify-between mb-2">
                <p
                  className="text-[11px] uppercase tracking-[0.25em]"
                  style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
                >
                  {formatDate(e.createdAt)}
                </p>
                <button
                  onClick={() => handleDelete(e.id)}
                  aria-label="Delete entry"
                  className="w-8 h-8 rounded-full flex items-center justify-center un-press"
                  style={{
                    background: "rgba(117,55,66,0.06)",
                    color: "var(--un-burgundy)",
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {e.prompt && (
                <p
                  className="italic text-[15px] mb-1.5"
                  style={{
                    color: "var(--un-burgundy)",
                    fontFamily: "Cormorant Garamond, serif",
                  }}
                >
                  {e.prompt}
                </p>
              )}
              <p
                className="text-[15px] leading-relaxed whitespace-pre-wrap"
                style={{ color: "var(--un-text-1)" }}
              >
                {e.text}
              </p>

              {/* Reflection — companion response */}
              {e.reflection ? (
                <motion.div
                  data-testid={`${JOURNAL_REFLECT.responsePrefix}${e.id}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-4 rounded-2xl px-5 py-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #F3E9DF 0%, #E9D9E0 100%)",
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles
                      size={10}
                      strokeWidth={1.6}
                      style={{ color: "var(--un-burgundy)" }}
                    />
                    <span
                      className="text-[10px] uppercase tracking-[0.28em]"
                      style={{
                        color: "var(--un-burgundy)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      Reflect
                    </span>
                  </div>
                  <p
                    className="leading-relaxed italic whitespace-pre-wrap"
                    style={{
                      color: "var(--un-text-1)",
                      fontFamily: "Cormorant Garamond, serif",
                      fontSize: "17px",
                    }}
                  >
                    {e.reflection}
                  </p>
                </motion.div>
              ) : reflectionCompanionAvailable ? (
                <button
                  data-testid={`${JOURNAL_REFLECT.triggerPrefix}${e.id}`}
                  onClick={() => handleReflect(e)}
                  disabled={loadingReflectId === e.id}
                  className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full un-press disabled:opacity-60"
                  style={{
                    background: "rgba(74,32,64,0.06)",
                    color: "var(--un-plum)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  {loadingReflectId === e.id ? (
                    <>
                      <motion.span
                        data-testid={`${JOURNAL_REFLECT.loadingPrefix}${e.id}`}
                        className="w-2 h-2 rounded-full"
                        style={{ background: "var(--un-plum)" }}
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      Listening…
                    </>
                  ) : (
                    <>
                      <Sparkles size={11} />
                      Reflect with me
                    </>
                  )}
                </button>
              ) : null}
            </motion.div>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
